import { useState, CSSProperties, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [regiao, setRegiao] = useState<string>("");

  // Função para verificar se é mobile baseado na largura da janela
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Verificar inicialmente
    checkIfMobile();
    
    // Adicionar listener para redimensionamento
    window.addEventListener('resize', checkIfMobile);
    
    // Limpeza ao desmontar
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Função para detectar a região com base no atributo data-regiao
  useEffect(() => {
    const detectarRegiao = () => {
      const regiaoAtributo = document.documentElement.getAttribute('data-regiao');
      if (regiaoAtributo) {
        setRegiao(regiaoAtributo);
      }
    };
    
    // Verificar inicialmente
    detectarRegiao();
    
    // Criar um observador de atributos para detectar mudanças no atributo data-regiao
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' && 
          mutation.attributeName === 'data-regiao'
        ) {
          detectarRegiao();
        }
      });
    });
    
    // Iniciar observação
    observer.observe(document.documentElement, { attributes: true });
    
    // Limpeza ao desmontar
    return () => {
      observer.disconnect();
    };
  }, []);

  // Verificar posição de scroll para mudar o estilo do navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    // Adicionar listener para scroll
    window.addEventListener('scroll', handleScroll);
    
    // Limpeza ao desmontar
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Impedir o scroll quando o menu mobile estiver aberto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const menuItems = [
    { href: '#about', text: 'Sobre' },
    { href: '#timeline', text: 'Cronograma' },
    { href: '#rules', text: 'Regras' },
    { href: '#awards', text: 'Premiação' },
    { href: '#register', text: 'Inscrição' },
  ];

  const navbarStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 150,
    backgroundColor: scrolled ? 'rgba(30, 58, 138, 0.9)' : 'transparent',
    backdropFilter: scrolled ? 'blur(8px)' : 'none',
    boxShadow: scrolled ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
    transition: 'all 0.3s ease-in-out',
    height: scrolled ? '70px' : '90px',
  };

  const containerStyle: CSSProperties = {
    width: '100%',
    maxWidth: '1280px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: scrolled ? '0.5rem 1rem' : '1rem',
    transition: 'padding 0.3s ease-in-out',
    height: '100%',
  };

  const flexBetweenStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    height: '100%',
  };

  const logoContainerStyle: CSSProperties = {
    position: 'relative',
    zIndex: 10,
    transform: scrolled ? 'scale(0.9)' : 'scale(1)',
    transition: 'transform 0.3s ease-in-out',
  };

  const logoStyle: CSSProperties = {
    position: 'relative',
    height: scrolled ? '40px' : '48px',
    width: scrolled ? '135px' : '160px',
    transition: 'all 0.3s ease-in-out',
  };

  const logoImgStyle: CSSProperties = {
    objectFit: 'contain',
    width: '100%',
    height: '100%',
  };

  const desktopMenuStyle: CSSProperties = {
    display: isMobile ? 'none' : 'flex',
    alignItems: 'center',
    gap: scrolled ? '1.5rem' : '2rem',
    paddingRight: '1.5rem',
    transition: 'gap 0.3s ease-in-out',
  };

  const linkStyle: CSSProperties = {
    color: 'white',
    fontSize: scrolled ? '0.8rem' : '0.875rem',
    fontWeight: 500,
    transition: 'all 0.3s ease-in-out',
  };

  const buttonStyle: CSSProperties = {
    display: 'inline-block',
    backgroundColor: '#ffb03f',
    color: 'white',
    padding: '0.625rem 1.5rem',
    borderRadius: '9999px',
    fontWeight: 500,
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  };

  const mobileMenuOverlayStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: isMobile && isMenuOpen ? 'block' : 'none',
    zIndex: 120,
  };

  const mobileMenuStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '65%',
    maxWidth: '250px',
    height: '100vh',
    backgroundColor: '#1e3a8a',
    backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #172554 100%)',
    boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.2)',
    zIndex: 140,
    padding: '5rem 2rem 2rem',
    transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 0.3s ease-in-out',
  };
  
  const mobileButtonContainerStyle: CSSProperties = {
    display: isMobile ? 'flex' : 'none',
    flexDirection: 'column',
    gap: '0.375rem',
    padding: '0.75rem',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    zIndex: 120,
    position: 'absolute',
    right: '1.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
  };

  const barStyle: CSSProperties = {
    width: '24px', 
    height: '2px', 
    backgroundColor: 'white', 
    borderRadius: '9999px', 
    display: 'block',
    transition: 'transform 0.3s, opacity 0.3s',
  };

  const mobileMenuItemsStyle: CSSProperties = {
    display: 'flex', 
    flexDirection: 'column', 
    gap: '1.5rem',
  };

  const mobileLinkStyle: CSSProperties = {
    ...linkStyle,
    fontSize: '1.125rem',
    display: 'block',
    padding: '0.5rem 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  };

  const mobileButtonWrapperStyle: CSSProperties = {
    marginTop: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
  };

  const mobileButtonStyle: CSSProperties = {
    ...buttonStyle,
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    width: '100%',
    textAlign: 'center',
  };

  const mobileCTAStyle: CSSProperties = {
    display: isMobile ? 'flex' : 'none',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    zIndex: 60,
    position: 'absolute',
    right: '1.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
  };

  const navLinksStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    gap: '1.5rem',
  };

  const isDesktop = !isMobile;

  return (
    <>
      <AnimatePresence>
        <motion.nav 
          style={navbarStyle}
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div style={containerStyle}>
            <div style={flexBetweenStyle}>
              <motion.a 
                href="/" 
                style={logoContainerStyle}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div style={logoStyle}>
                  <img 
                    alt="Happy Code"
                    loading="lazy"
                    style={logoImgStyle}
                    src="/images/realizadores/happy-logo.png"
                  />
                </div>
              </motion.a>

              {/* Menu Desktop */}
              <div style={desktopMenuStyle}>
                {isDesktop && (
                  <div style={navLinksStyle}>
                    <a href="#about" style={linkStyle}>Sobre</a>
                    <a href="#rules" style={linkStyle}>Regras</a>
                    <a href="#timeline" style={linkStyle}>Cronograma</a>
                    <a href="#awards" style={linkStyle}>Premiação</a>
                    <a href="#register" style={linkStyle}>Inscrição</a>
                  </div>
                )}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <motion.a
                    href="#timeline"
                    style={{
                      ...buttonStyle,
                      marginLeft: '0.75rem',
                      display: isDesktop ? 'block' : 'none',
                    }}
                  >
                    Etapas
                  </motion.a>
                </motion.div>
              </div>

              {/* Botão Menu Mobile */}
              <button
                style={{
                  ...mobileButtonContainerStyle,
                  position: 'absolute',
                  zIndex: 200
                }}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                <span style={{
                  ...barStyle,
                  transform: isMenuOpen ? 'translateY(8px) rotate(45deg)' : 'none'
                }}></span>
                <span style={{
                  ...barStyle,
                  opacity: isMenuOpen ? 0 : 1
                }}></span>
                <span style={{
                  ...barStyle,
                  transform: isMenuOpen ? 'translateY(-8px) rotate(-45deg)' : 'none'
                }}></span>
              </button>
            </div>
          </div>
        </motion.nav>
      </AnimatePresence>

      {/* Overlay e Menu Mobile */}
      <div style={mobileMenuOverlayStyle} onClick={() => setIsMenuOpen(false)}></div>
      <div style={mobileMenuStyle}>
        <div style={mobileMenuItemsStyle}>
          <a 
            href="#about" 
            style={mobileLinkStyle}
            onClick={() => setIsMenuOpen(false)}
          >
            Sobre
          </a>
          <a 
            href="#rules" 
            style={mobileLinkStyle}
            onClick={() => setIsMenuOpen(false)}
          >
            Regras
          </a>
          <a 
            href="#timeline" 
            style={mobileLinkStyle}
            onClick={() => setIsMenuOpen(false)}
          >
            Cronograma
          </a>
          <a 
            href="#awards" 
            style={mobileLinkStyle}
            onClick={() => setIsMenuOpen(false)}
          >
            Premiação
          </a>
          <a 
            href="#register" 
            style={mobileLinkStyle}
            onClick={() => setIsMenuOpen(false)}
          >
            Inscrição
          </a>
          <div style={{marginTop: '0.75rem', textAlign: 'center', width: '100%'}}>
            <a 
              href="#timeline" 
              style={{
                ...buttonStyle,
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                display: 'block',
                width: '100%',
                boxSizing: 'border-box'
              }}
              onClick={() => setIsMenuOpen(false)}
            >
              Etapas
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar; 